import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import CFSelect from "./CFSelect";
import TooltipHover from "../TooltipHover";
import mergeTagUpgradeGate from "../../../utils/mergeTagUpgradeGate";
import {
  FIELD_KEY,
  createMergeTagChip,
  fieldKey,
  findMergeTagOption,
  namedPrefix,
  parseMergeTag,
  serializeMergeTag,
  setMergeTagChip,
} from "../../../utils/mergeTagChips";

const mergeTagFilter = (option, input) => {
  const haystack = [option.label, option.value].join(" ").toLowerCase();
  return input
    .toLowerCase()
    .split(/\s+/)
    .every((word) => haystack.includes(word));
};

const pickerClassNames = {
  control: () => "!rounded-none !border-0 !border-b",
  menu: () => "!static !m-0 !rounded-none !border-0 !shadow-none",
};

const pickerComponents = { DropdownIndicator: null, IndicatorSeparator: null };

const POPOVER_WIDTH = 280;

const openPopover = (froala, rect, className, render, onClose) => {
  const container = document.createElement("div");
  container.className =
    "fixed z-[2000] text-xl text-black dark:text-white rounded-xl border border-solid border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 shadow-lg " +
    className;
  container.style.minWidth = POPOVER_WIDTH + "px";
  container.style.width = "max-content";
  container.style.top = rect.bottom + 4 + "px";
  container.style.left =
    Math.max(0, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8)) +
    "px";
  document.body.appendChild(container);

  const root = createRoot(container);
  froala.events.disableBlur();

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    setTimeout(() => root.unmount());
    container.remove();
    froala.events.enableBlur();
    onClose();
  };

  root.render(render(container));
  return { close, container };
};

export function openMergeTagPicker(froala, groups, website) {
  const button = froala.$tb?.get(0)?.querySelector('[data-cmd="personalize"]');
  const rect = button?.getBoundingClientRect() || { left: 0, bottom: 0 };
  froala.selection.save();

  const insert = (option) => {
    close();
    if (!option || !mergeTagUpgradeGate(option, website)) return;
    froala.html.insert(
      createMergeTagChip(document, option.value, groups).outerHTML
    );
    froala.undo.saveStep();
  };

  const { close } = openPopover(
    froala,
    rect,
    "overflow-hidden",
    () => (
      <CFSelect
        autoFocus
        menuIsOpen
        placeholder="Search merge tags..."
        options={groups}
        filterOption={mergeTagFilter}
        classNames={pickerClassNames}
        components={pickerComponents}
        value={null}
        onChange={insert}
        onMenuClose={() => close()}
      />
    ),
    () => froala.selection.restore()
  );
}

const FORMAT_OPTIONS = [
  { value: "field_labels", label: "Answer Label" },
  { value: "field_values", label: "Answer Value" },
];
const LEGACY_FORMAT = { value: "fields", label: "Legacy" };

const fieldClass = "flex flex-col gap-2";
const inputClass =
  "w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none";
const stripReserved = (value) => value.replace(/[{}()]/g, "");

const AFFIX_FIELDS = [
  [
    "before",
    "Text Before",
    "Shown before the value. Only shows when a value exists.",
  ],
  [
    "after",
    "Text After",
    "Shown after the value. Only shows when a value exists.",
  ],
  [
    "fallback",
    "Fallback",
    "Shown instead (without text before or after) when there is no value.",
  ],
];

export const MergeTagChipSettings = ({
  initialTag,
  groups,
  website,
  onChange,
  onRemove,
  menuPortalTarget,
}) => {
  const [tag, setTag] = useState(initialTag);
  const prefix = namedPrefix(tag.key);
  const field = tag.key.match(FIELD_KEY);
  const formats =
    field?.[1] === "fields"
      ? [LEGACY_FORMAT, ...FORMAT_OPTIONS]
      : FORMAT_OPTIONS;

  const update = (next) => {
    setTag(next);
    onChange(next);
  };

  const changeTag = (option) => {
    if (!option || !mergeTagUpgradeGate(option, website)) return;
    const next = parseMergeTag(option.value);
    const nextField = next.key.match(FIELD_KEY);
    const format = field && field[1] !== "fields" ? field[1] : "field_labels";
    update({
      key: nextField ? fieldKey(format, nextField[2]) : next.key,
      before: tag.before,
      after: tag.after,
      fallback: tag.fallback !== undefined ? tag.fallback : next.fallback,
    });
  };

  const changeName = (value) => {
    const next = {
      ...tag,
      key: prefix + stripReserved(value).replace(/\s/g, ""),
    };
    setTag(next);
    if (next.key !== prefix) onChange(next);
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <label className={fieldClass}>
        Field To Show
        <CFSelect
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          menuShouldScrollIntoView={false}
          options={groups}
          value={findMergeTagOption(groups, tag.key)?.option || null}
          onChange={changeTag}
        />
      </label>
      {field && (
        <label className={fieldClass}>
          <span>
            Answer Format
            <TooltipHover
              inline
              icon={["fas", "circle-info"]}
              tooltip="Answer Label shows the selected option's label; Answer Value shows the raw saved value."
            />
          </span>
          <CFSelect
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            menuShouldScrollIntoView={false}
            options={formats}
            value={formats.find((format) => format.value === field[1])}
            onChange={(format) =>
              update({
                ...tag,
                key: fieldKey(format.value, field[2]),
              })
            }
          />
        </label>
      )}
      {prefix && (
        <label className={fieldClass}>
          Name
          <input
            type="text"
            className={inputClass}
            value={tag.key.slice(prefix.length)}
            onChange={(e) => changeName(e.target.value)}
          />
        </label>
      )}
      {(tag.key.startsWith("ai.") ? AFFIX_FIELDS.slice(2) : AFFIX_FIELDS).map(
        ([attr, label, tooltip]) => (
          <label key={attr} className={fieldClass}>
            <span>
              {label}
              <TooltipHover
                inline
                icon={["fas", "circle-info"]}
                tooltip={tooltip}
              />
            </span>
            <input
              type="text"
              className={inputClass}
              value={tag[attr] || ""}
              onChange={(e) =>
                update({ ...tag, [attr]: stripReserved(e.target.value) })
              }
            />
          </label>
        )
      )}
      <button
        type="button"
        className="self-start text-red-600 bg-transparent border-0 p-0 cursor-pointer"
        onClick={onRemove}
      >
        Remove
      </button>
    </div>
  );
};

const editChip = (froala, edit) => {
  froala.undo.saveStep();
  edit();
  froala.undo.saveStep();
  froala.events.trigger("contentChanged");
};

export function removeMergeTagChip(froala, chip) {
  if (openChip?.chip === chip) openChip.close();
  editChip(froala, () => chip.remove());
}

let openChip = null;

export const isMergeTagChipPopoverOpen = (froala) =>
  openChip?.froala === froala;

export function openMergeTagChipPopover(froala, chip, groups, website) {
  if (openChip?.chip === chip) {
    openChip.close();
    return;
  }
  const initialTag = parseMergeTag(chip.dataset.mergeTag);
  if (!initialTag) return;

  const onOutside = (e) => {
    if (!popover.container.contains(e.target) && !chip.contains(e.target)) {
      popover.close();
    }
  };
  const onEscape = (e) => {
    if (e.key !== "Escape") return;
    e.stopPropagation();
    popover.close();
  };

  const popover = openPopover(
    froala,
    chip.getBoundingClientRect(),
    "",
    (container) => (
      <MergeTagChipSettings
        menuPortalTarget={container}
        initialTag={initialTag}
        groups={groups}
        website={website}
        onChange={(tag) => {
          editChip(froala, () =>
            setMergeTagChip(chip, serializeMergeTag(tag), groups)
          );
        }}
        onRemove={() => {
          removeMergeTagChip(froala, chip);
          popover.close();
        }}
      />
    ),
    () => {
      openChip = null;
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("keydown", onEscape, true);
      froala.events.focus();
    }
  );

  if (froala.opts.toolbarInline) froala.toolbar.hide();
  openChip = { froala, chip, close: popover.close };
  document.addEventListener("mousedown", onOutside, true);
  document.addEventListener("keydown", onEscape, true);
}
